import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { PortalRoutes } from './portals/PortalRoutes';
import { LoginPage } from './portals/admin/pages/LoginPage';
import ForgotPasswordPage from './portals/admin/pages/ForgotPasswordPage';
import ResetPasswordPage from './portals/admin/pages/ResetPasswordPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import { POSProvider } from './contexts/POSContext';
import { UpdateBanner } from './components/UpdateBanner';
import PortalSelector from './components/PortalSelector';
import Splash from './components/Splash';

// Ambassador Portal
import POSProposals from './portals/admin/pages/POSProposals';
import AmbassadorLogin from './portals/ambassador/AmbassadorLogin';
import AmbassadorAutoSignIn from './portals/ambassador/AmbassadorAutoSignIn';
import AmbassadorLayout from './portals/ambassador/AmbassadorLayout';
import AmbassadorDashboard from './portals/ambassador/AmbassadorDashboard';
import AmbassadorPayoutSetup from './portals/ambassador/AmbassadorPayoutSetup';
import AmbassadorSubmitProperty from './portals/ambassador/AmbassadorSubmitProperty';
import AmbassadorMyProperties from './portals/ambassador/AmbassadorMyProperties';
import AmbassadorTraining from './portals/ambassador/AmbassadorTraining';

// Training Viewer (standalone layout)
import TrainingLayout from './portals/training/TrainingLayout';
import { TrainingRoutes } from './portals/training/TrainingPage';

// POS Hotel Staff Portal
import POSLogin from './portals/pos/pages/POSLogin';
import POSWelcome from './portals/pos/pages/POSWelcome';
import POSPropertySelector from './portals/pos/pages/POSPropertySelector';
import POSLayout from './portals/pos/POSLayout';
import POSTeam from './portals/pos/pages/POSTeam';
import POSGroupDashboard from './portals/pos/pages/POSGroupDashboard';
// POS pages reused from admin
import POSDashboardPage from './portals/admin/pages/POSDashboard';
import POSServiceManagementPage from './portals/admin/pages/POSServiceManagement';
import POSRoomsPage from './portals/admin/pages/POSRooms';
import POSPropertiesPage from './portals/admin/pages/pos-properties/POSProperties';
import POSCertificatesPage from './portals/admin/pages/POSCertificates';

// Clear recovery sessionStorage flags on successful app boot.
// If JS is running, we are past the failure modes these guards protected against,
// so the safety watchdogs can fire again later in the session if needed.
sessionStorage.removeItem('chunk_reload');
sessionStorage.removeItem('splash_reload');
sessionStorage.removeItem('sw-recovery-attempted');

const PORTAL_STORAGE_KEY = 'sentinel_last_portal';

function PortalGate() {
  const { getPortalAccess } = useAuth();
  const access = getPortalAccess();
  const roleCount = [access.admin, access.pos, access.ambassador].filter(Boolean).length;

  // Single role → straight to that portal (not always "/" which is admin).
  if (roleCount === 1) {
    if (access.ambassador) return <Navigate to="/ambassador" replace />;
    if (access.pos) return <Navigate to="/pos" replace />;
    return <Navigate to="/" replace />;
  }

  // Multi-role with a remembered preference → use it.
  const stored = localStorage.getItem(PORTAL_STORAGE_KEY);
  if (stored === 'admin' && access.admin) return <Navigate to="/" replace />;
  if (stored === 'pos' && access.pos) return <Navigate to="/pos" replace />;
  if (stored === 'ambassador' && access.ambassador) return <Navigate to="/ambassador" replace />;

  return <PortalSelector />;
}

/**
 * HomeGate — wraps the admin catch-all route. If a logged-in user has no
 * admin access but does have ambassador or POS access, route them to that
 * portal instead of dumping them into the admin shell where they don't
 * belong. Without this, a single-role ambassador signing in via /login
 * lands on the ops dashboard with read errors everywhere.
 */
function HomeGate({ children }: { children: React.ReactNode }) {
  const { getPortalAccess } = useAuth();
  const access = getPortalAccess();
  if (!access.admin) {
    if (access.ambassador) return <Navigate to="/ambassador" replace />;
    if (access.pos) return <Navigate to="/pos" replace />;
  }
  return <>{children}</>;
}

function AppContent() {
  const { session, loading } = useAuth();

  // Render the same splash the HTML showed while the JS was downloading,
  // so the handoff is seamless. Returning null here left the screen blank
  // for the duration of the auth bootstrap (getSession + profile fetch,
  // 200ms-2s on slow networks), which is the "blank state with no feedback"
  // PWA users were reporting.
  if (loading) return <Splash />;

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/ambassador/login" element={<AmbassadorLogin />} />
        <Route path="/ambassador/auto-signin" element={<AmbassadorAutoSignIn />} />
        {/* POS Hotel Staff Portal - public routes */}
        <Route path="/pos/login" element={<POSLogin />} />
        <Route path="/pos/welcome" element={<POSWelcome />} />
        {/* Training Viewer — public read for property-staff share links.
            RLS gates the data: anonymous visitors can only see content
            reachable from an active property_staff course. */}
        <Route path="/training/*" element={
          <TrainingLayout>
            <TrainingRoutes />
          </TrainingLayout>
        } />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Portal Selector */}
      <Route path="/select-portal" element={<PortalGate />} />
      {/* Ambassador Portal — own layout, outside AdminLayout.
          All routes wrap in ConfigProvider because pages use useConfig()
          for currency / locale formatting via country_config. */}
      <Route path="/ambassador" element={
        <ConfigProvider>
          <LayoutProvider>
            <AmbassadorLayout>
              <AmbassadorDashboard />
            </AmbassadorLayout>
          </LayoutProvider>
        </ConfigProvider>
      } />
      <Route path="/ambassador/submit-property" element={
        <ConfigProvider>
          <LayoutProvider>
            <AmbassadorLayout>
              <AmbassadorSubmitProperty />
            </AmbassadorLayout>
          </LayoutProvider>
        </ConfigProvider>
      } />
      <Route path="/ambassador/properties" element={
        <ConfigProvider>
          <LayoutProvider>
            <AmbassadorLayout>
              <AmbassadorMyProperties />
            </AmbassadorLayout>
          </LayoutProvider>
        </ConfigProvider>
      } />
      <Route path="/ambassador/proposals" element={
        <ConfigProvider>
          <LayoutProvider>
            <AmbassadorLayout>
              <POSProposals />
            </AmbassadorLayout>
          </LayoutProvider>
        </ConfigProvider>
      } />
      <Route path="/ambassador/payout-setup" element={
        <ConfigProvider>
          <LayoutProvider>
            <AmbassadorLayout>
              <AmbassadorPayoutSetup />
            </AmbassadorLayout>
          </LayoutProvider>
        </ConfigProvider>
      } />
      <Route path="/ambassador/training" element={
        <ConfigProvider>
          <LayoutProvider>
            <AmbassadorLayout>
              <AmbassadorTraining />
            </AmbassadorLayout>
          </LayoutProvider>
        </ConfigProvider>
      } />
      <Route path="/ambassador/login" element={<AmbassadorLogin />} />
      {/* POS Hotel Staff Portal — own layout, outside AdminLayout */}
      <Route path="/pos/login" element={<POSLogin />} />
      <Route path="/pos/welcome" element={<POSWelcome />} />
      <Route path="/pos" element={
        <ConfigProvider>
          <POSProvider>
            <POSPropertySelector />
          </POSProvider>
        </ConfigProvider>
      } />
      <Route path="/pos/group/:customerSlug" element={
        <ConfigProvider>
          <POSProvider>
            <POSGroupDashboard />
          </POSProvider>
        </ConfigProvider>
      } />
      <Route path="/pos/:propertySlug" element={
        <ConfigProvider>
          <POSProvider>
            <POSLayout><POSDashboardPage /></POSLayout>
          </POSProvider>
        </ConfigProvider>
      } />
      <Route path="/pos/:propertySlug/services" element={
        <ConfigProvider>
          <POSProvider>
            <POSLayout><POSServiceManagementPage /></POSLayout>
          </POSProvider>
        </ConfigProvider>
      } />
      <Route path="/pos/:propertySlug/rooms" element={
        <ConfigProvider>
          <POSProvider>
            <POSLayout><POSRoomsPage /></POSLayout>
          </POSProvider>
        </ConfigProvider>
      } />
      <Route path="/pos/:propertySlug/properties" element={
        <ConfigProvider>
          <POSProvider>
            <POSLayout><POSPropertiesPage /></POSLayout>
          </POSProvider>
        </ConfigProvider>
      } />
      <Route path="/pos/:propertySlug/managers" element={
        <ConfigProvider>
          <POSProvider>
            <POSLayout><POSTeam /></POSLayout>
          </POSProvider>
        </ConfigProvider>
      } />
      <Route path="/pos/:propertySlug/certificates" element={
        <ConfigProvider>
          <POSProvider>
            <POSLayout><POSCertificatesPage /></POSLayout>
          </POSProvider>
        </ConfigProvider>
      } />
      {/* Training Viewer — standalone layout, no admin sidebar */}
      <Route path="/training/*" element={
        <TrainingLayout>
          <TrainingRoutes />
        </TrainingLayout>
      } />
      {/* Main Portal */}
      <Route path="*" element={
        <HomeGate>
          <ConfigProvider>
            <LayoutProvider>
              <POSProvider>
                <PortalRoutes />
              </POSProvider>
            </LayoutProvider>
          </ConfigProvider>
        </HomeGate>
      } />
    </Routes>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <UpdateBanner />
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
